import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import WebService from "../Services/WebService";
import styles from "./StateCityManager.module.css";

type Option = { id: number; name: string; code?: string; phone?: string };

function asList(res: any): any[] {
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  return [];
}

function optionId(row: any, ...keys: string[]): number {
  for (const key of keys) {
    const value = row?.[key];
    if (value != null && value !== "") return Number(value);
  }
  return 0;
}

function optionName(row: any, ...keys: string[]): string {
  for (const key of keys) {
    const value = row?.[key];
    if (value != null && String(value).trim()) return String(value).trim();
  }
  return "";
}

const StateCityManager: React.FC = () => {
  const [countries, setCountries] = useState<Option[]>([]);
  const [countryId, setCountryId] = useState<number | "">("");
  const [states, setStates] = useState<Option[]>([]);
  const [cities, setCities] = useState<Option[]>([]);
  const [selectedStateId, setSelectedStateId] = useState<number | null>(null);

  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [saving, setSaving] = useState(false);

  const [newStateName, setNewStateName] = useState("");
  const [newCityName, setNewCityName] = useState("");
  const [editingStateId, setEditingStateId] = useState<number | null>(null);
  const [editingCityId, setEditingCityId] = useState<number | null>(null);
  const [editStateName, setEditStateName] = useState("");
  const [editCityName, setEditCityName] = useState("");

  const [showManageCountry, setShowManageCountry] = useState(false);
  const [newCountryName, setNewCountryName] = useState("");
  const [newCountryCode, setNewCountryCode] = useState("");
  const [newCountryPhone, setNewCountryPhone] = useState("");
  const [editingCountryId, setEditingCountryId] = useState<number | null>(null);
  const [editCountryName, setEditCountryName] = useState("");
  const [editCountryCode, setEditCountryCode] = useState("");
  const [editCountryPhone, setEditCountryPhone] = useState("");

  const selectedState = states.find((s) => s.id === selectedStateId) || null;

  const mapCountries = (res: any): Option[] =>
    asList(res)
      .map((c: any) => ({
        id: optionId(c, "id", "country_id"),
        name: optionName(c, "name", "country_name"),
        code: optionName(c, "country_code", "code"),
        phone: optionName(c, "phone_code", "phone"),
      }))
      .filter((c: Option) => c.id && c.name);

  const applyCountryList = (mapped: Option[], preferId?: number | "") => {
    setCountries(mapped);
    const current = preferId !== undefined ? preferId : countryId;
    if (current && mapped.some((c) => c.id === current)) {
      if (preferId !== undefined) setCountryId(preferId);
      return;
    }
    const india = mapped.find((c) => c.name.toLowerCase() === "india");
    setCountryId((india ?? mapped[0])?.id ?? "");
  };

  const reloadCountries = async (preferId?: number | "") => {
    const res: any = await WebService.getAPI({
      action: "/v1/door-management/users/countries",
      isShowError: false,
    });
    applyCountryList(mapCountries(res), preferId);
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingCountries(true);
      try {
        const res: any = await WebService.getAPI({
          action: "/v1/door-management/users/countries",
          isShowError: false,
        });
        if (!mounted) return;
        applyCountryList(mapCountries(res));
      } catch {
        if (mounted) setCountries([]);
      } finally {
        if (mounted) setLoadingCountries(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!countryId) {
      setStates([]);
      setSelectedStateId(null);
      return;
    }
    let mounted = true;
    (async () => {
      setLoadingStates(true);
      setSelectedStateId(null);
      setCities([]);
      try {
        const res: any = await WebService.getAPI({
          action: `/v1/door-management/users/countries/${countryId}/states`,
          isShowError: false,
        });
        if (!mounted) return;
        setStates(
          asList(res)
            .map((s: any) => ({
              id: optionId(s, "id", "state_id"),
              name: optionName(s, "name", "state_name"),
            }))
            .filter((s: Option) => s.id && s.name)
        );
      } catch {
        if (mounted) setStates([]);
      } finally {
        if (mounted) setLoadingStates(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [countryId]);

  useEffect(() => {
    if (!selectedStateId) {
      setCities([]);
      return;
    }
    let mounted = true;
    (async () => {
      setLoadingCities(true);
      try {
        const res: any = await WebService.getAPI({
          action: `/v1/door-management/users/states/${selectedStateId}/cities`,
          isShowError: false,
        });
        if (!mounted) return;
        setCities(
          asList(res)
            .map((c: any) => ({
              id: optionId(c, "id", "city_id"),
              name: optionName(c, "name", "city_name"),
            }))
            .filter((c: Option) => c.id && c.name)
        );
      } catch {
        if (mounted) setCities([]);
      } finally {
        if (mounted) setLoadingCities(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [selectedStateId]);

  const reloadStates = async (keepSelected = true) => {
    if (!countryId) return;
    const res: any = await WebService.getAPI({
      action: `/v1/door-management/users/countries/${countryId}/states`,
      isShowError: false,
    });
    const next = asList(res)
      .map((s: any) => ({
        id: optionId(s, "id", "state_id"),
        name: optionName(s, "name", "state_name"),
      }))
      .filter((s: Option) => s.id && s.name);
    setStates(next);
    if (keepSelected && selectedStateId && !next.some((s) => s.id === selectedStateId)) {
      setSelectedStateId(null);
    }
  };

  const reloadCities = async () => {
    if (!selectedStateId) return;
    const res: any = await WebService.getAPI({
      action: `/v1/door-management/users/states/${selectedStateId}/cities`,
      isShowError: false,
    });
    setCities(
      asList(res)
        .map((c: any) => ({
          id: optionId(c, "id", "city_id"),
          name: optionName(c, "name", "city_name"),
        }))
        .filter((c: Option) => c.id && c.name)
    );
  };

  const addState = async () => {
    const name = newStateName.trim();
    if (!countryId) {
      toast.error("Select a country first");
      return;
    }
    if (!name) {
      toast.error("Enter a state name");
      return;
    }
    setSaving(true);
    try {
      const res: any = await WebService.postAPI({
        action: `/v1/door-management/users/countries/${countryId}/states`,
        body: { state_name: name },
        isShowError: false,
      });
      if (res?.status_code === 201 || res?.status_code === 200) {
        setNewStateName("");
        await reloadStates();
        const createdId = optionId(res?.data, "id", "state_id");
        if (createdId) setSelectedStateId(createdId);
        toast.success("State added");
      } else {
        toast.error(res?.message || "Failed to add state");
      }
    } catch (err: any) {
      toast.error(typeof err === "string" ? err : "Failed to add state");
    } finally {
      setSaving(false);
    }
  };

  const saveState = async (id: number) => {
    const name = editStateName.trim();
    if (!name) {
      toast.error("State name is required");
      return;
    }
    setSaving(true);
    try {
      const res: any = await WebService.putAPI({
        action: `/v1/door-management/users/states/${id}`,
        body: { state_name: name },
        isShowError: false,
      });
      if (res?.status_code === 200) {
        setEditingStateId(null);
        await reloadStates();
        toast.success("State updated");
      } else {
        toast.error(res?.message || "Failed to update state");
      }
    } catch (err: any) {
      toast.error(typeof err === "string" ? err : "Failed to update state");
    } finally {
      setSaving(false);
    }
  };

  const removeState = async (state: Option) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Delete State",
      text: `Delete "${state.name}"? A state can be deleted only if it has no cities.`,
      showCancelButton: true,
      confirmButtonColor: "#e74c3c",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Yes, Delete",
      reverseButtons: true,
    });
    if (!confirm.isConfirmed) return;
    setSaving(true);
    try {
      const res: any = await WebService.deleteAPI({
        action: `/v1/door-management/users/states/${state.id}`,
      });
      if (res?.status_code === 200) {
        if (selectedStateId === state.id) setSelectedStateId(null);
        await reloadStates(false);
        toast.success("State deleted");
      } else {
        toast.error(res?.message || "Failed to delete state");
      }
    } catch {
      // deleteAPI already toasts
    } finally {
      setSaving(false);
    }
  };

  const addCity = async () => {
    const name = newCityName.trim();
    if (!selectedStateId) {
      toast.error("Select a state first");
      return;
    }
    if (!name) {
      toast.error("Enter a city name");
      return;
    }
    setSaving(true);
    try {
      const res: any = await WebService.postAPI({
        action: `/v1/door-management/users/states/${selectedStateId}/cities`,
        body: { city_name: name },
        isShowError: false,
      });
      if (res?.status_code === 201 || res?.status_code === 200) {
        setNewCityName("");
        await reloadCities();
        toast.success("City added");
      } else {
        toast.error(res?.message || "Failed to add city");
      }
    } catch (err: any) {
      toast.error(typeof err === "string" ? err : "Failed to add city");
    } finally {
      setSaving(false);
    }
  };

  const saveCity = async (id: number) => {
    const name = editCityName.trim();
    if (!name) {
      toast.error("City name is required");
      return;
    }
    setSaving(true);
    try {
      const res: any = await WebService.putAPI({
        action: `/v1/door-management/users/cities/${id}`,
        body: { city_name: name },
        isShowError: false,
      });
      if (res?.status_code === 200) {
        setEditingCityId(null);
        await reloadCities();
        toast.success("City updated");
      } else {
        toast.error(res?.message || "Failed to update city");
      }
    } catch (err: any) {
      toast.error(typeof err === "string" ? err : "Failed to update city");
    } finally {
      setSaving(false);
    }
  };

  const removeCity = async (city: Option) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Delete City",
      text: `Delete "${city.name}"?`,
      showCancelButton: true,
      confirmButtonColor: "#e74c3c",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Yes, Delete",
      reverseButtons: true,
    });
    if (!confirm.isConfirmed) return;
    setSaving(true);
    try {
      const res: any = await WebService.deleteAPI({
        action: `/v1/door-management/users/cities/${city.id}`,
      });
      if (res?.status_code === 200) {
        await reloadCities();
        toast.success("City deleted");
      } else {
        toast.error(res?.message || "Failed to delete city");
      }
    } catch {
      // deleteAPI already toasts
    } finally {
      setSaving(false);
    }
  };

  const addCountry = async () => {
    const name = newCountryName.trim();
    if (!name) {
      toast.error("Enter a country name");
      return;
    }
    setSaving(true);
    try {
      const body: Record<string, string> = { country_name: name };
      if (newCountryCode.trim()) body.country_code = newCountryCode.trim();
      if (newCountryPhone.trim()) body.phone_code = newCountryPhone.trim();
      const res: any = await WebService.postAPI({
        action: "/v1/door-management/users/countries",
        body,
        isShowError: false,
      });
      if (res?.status_code === 201 || res?.status_code === 200) {
        setNewCountryName("");
        setNewCountryCode("");
        setNewCountryPhone("");
        const createdId = optionId(res?.data, "id", "country_id");
        await reloadCountries(createdId || undefined);
        toast.success("Country added");
      } else {
        toast.error(res?.message || "Failed to add country");
      }
    } catch (err: any) {
      toast.error(typeof err === "string" ? err : "Failed to add country");
    } finally {
      setSaving(false);
    }
  };

  const saveCountry = async (id: number) => {
    const name = editCountryName.trim();
    if (!name) {
      toast.error("Country name is required");
      return;
    }
    if (!editCountryCode.trim()) {
      toast.error("Country code is required");
      return;
    }
    if (!editCountryPhone.trim()) {
      toast.error("Phone code is required");
      return;
    }
    setSaving(true);
    try {
      const res: any = await WebService.putAPI({
        action: `/v1/door-management/users/countries/${id}`,
        body: {
          country_name: name,
          country_code: editCountryCode.trim(),
          phone_code: editCountryPhone.trim(),
        },
        isShowError: false,
      });
      if (res?.status_code === 200) {
        setEditingCountryId(null);
        await reloadCountries(countryId);
        toast.success("Country updated");
      } else {
        toast.error(res?.message || "Failed to update country");
      }
    } catch (err: any) {
      toast.error(typeof err === "string" ? err : "Failed to update country");
    } finally {
      setSaving(false);
    }
  };

  const removeCountry = async (country: Option) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Delete Country",
      text: `Delete "${country.name}"? A country can be deleted only if it has no states.`,
      showCancelButton: true,
      confirmButtonColor: "#e74c3c",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Yes, Delete",
      reverseButtons: true,
    });
    if (!confirm.isConfirmed) return;
    setSaving(true);
    try {
      const res: any = await WebService.deleteAPI({
        action: `/v1/door-management/users/countries/${country.id}`,
      });
      if (res?.status_code === 200) {
        if (editingCountryId === country.id) setEditingCountryId(null);
        await reloadCountries(countryId === country.id ? "" : countryId);
        toast.success("Country deleted");
      } else {
        toast.error(res?.message || "Failed to delete country");
      }
    } catch {
      // deleteAPI already toasts
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.wrap}>
      <label className={styles.label} htmlFor="state-city-country">
        Country
      </label>
      <div className={styles.countryRow}>
        <select
          id="state-city-country"
          className={styles.select}
          value={countryId}
          disabled={loadingCountries || saving}
          onChange={(e) => setCountryId(e.target.value ? Number(e.target.value) : "")}
        >
          <option value="">{loadingCountries ? "Loading countries…" : "Select country"}</option>
          {countries.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          className={styles.manageBtn}
          disabled={loadingCountries || saving}
          onClick={() => {
            setEditingCountryId(null);
            setShowManageCountry(true);
          }}
        >
          Manage Country
        </button>
      </div>

      <div className={styles.grid}>
        <section className={styles.panel}>
          <div className={styles.panelTitle}>States</div>
          <div className={styles.list}>
            {loadingStates ? (
              <div className={styles.empty}>Loading states…</div>
            ) : !countryId ? (
              <div className={styles.empty}>Select a country to manage states.</div>
            ) : states.length === 0 ? (
              <div className={styles.empty}>No states yet. Add one below.</div>
            ) : (
              states.map((state) => (
                <div
                  key={state.id}
                  className={`${styles.item} ${selectedStateId === state.id ? styles.itemActive : ""}`}
                >
                  {editingStateId === state.id ? (
                    <input
                      className={styles.inlineInput}
                      value={editStateName}
                      onChange={(e) => setEditStateName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveState(state.id);
                        if (e.key === "Escape") setEditingStateId(null);
                      }}
                      autoFocus
                    />
                  ) : (
                    <button
                      type="button"
                      className={styles.itemName}
                      onClick={() => {
                        setSelectedStateId(state.id);
                        setEditingCityId(null);
                      }}
                    >
                      {state.name}
                    </button>
                  )}
                  <div className={styles.itemActions}>
                    {editingStateId === state.id ? (
                      <>
                        <button type="button" className={styles.linkBtn} disabled={saving} onClick={() => saveState(state.id)}>
                          Save
                        </button>
                        <button type="button" className={styles.linkBtnMuted} onClick={() => setEditingStateId(null)}>
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className={styles.linkBtn}
                          disabled={saving}
                          onClick={() => {
                            setSelectedStateId(state.id);
                            setEditingStateId(state.id);
                            setEditStateName(state.name);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className={styles.linkBtnDanger}
                          disabled={saving}
                          onClick={() => removeState(state)}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
          <div className={styles.addRow}>
            <input
              className={styles.input}
              placeholder="New state name"
              value={newStateName}
              disabled={!countryId || saving}
              onChange={(e) => setNewStateName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") addState();
              }}
            />
            <button type="button" className={styles.addBtn} disabled={!countryId || saving} onClick={addState}>
              Add State
            </button>
          </div>
        </section>

        <section className={styles.panel}>
          <div className={styles.panelTitle}>
            {selectedState ? `Cities in ${selectedState.name}` : "Cities"}
          </div>
          <div className={styles.list}>
            {!selectedStateId ? (
              <div className={styles.empty}>Select a state to view and add its cities.</div>
            ) : loadingCities ? (
              <div className={styles.empty}>Loading cities…</div>
            ) : cities.length === 0 ? (
              <div className={styles.empty}>No cities in this state yet. Add one below.</div>
            ) : (
              cities.map((city) => (
                <div key={city.id} className={styles.item}>
                  {editingCityId === city.id ? (
                    <input
                      className={styles.inlineInput}
                      value={editCityName}
                      onChange={(e) => setEditCityName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveCity(city.id);
                        if (e.key === "Escape") setEditingCityId(null);
                      }}
                      autoFocus
                    />
                  ) : (
                    <span className={styles.itemLabel}>{city.name}</span>
                  )}
                  <div className={styles.itemActions}>
                    {editingCityId === city.id ? (
                      <>
                        <button type="button" className={styles.linkBtn} disabled={saving} onClick={() => saveCity(city.id)}>
                          Save
                        </button>
                        <button type="button" className={styles.linkBtnMuted} onClick={() => setEditingCityId(null)}>
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className={styles.linkBtn}
                          disabled={saving}
                          onClick={() => {
                            setEditingCityId(city.id);
                            setEditCityName(city.name);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className={styles.linkBtnDanger}
                          disabled={saving}
                          onClick={() => removeCity(city)}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
          <div className={styles.addRow}>
            <input
              className={styles.input}
              placeholder={selectedState ? `New city in ${selectedState.name}` : "Select a state first"}
              value={newCityName}
              disabled={!selectedStateId || saving}
              onChange={(e) => setNewCityName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") addCity();
              }}
            />
            <button type="button" className={styles.addBtn} disabled={!selectedStateId || saving} onClick={addCity}>
              Add City
            </button>
          </div>
        </section>
      </div>

      {showManageCountry && (
        <div
          className={styles.modalOverlay}
          onClick={() => {
            setShowManageCountry(false);
            setEditingCountryId(null);
          }}
        >
          <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Manage Countries</h3>
              <button
                type="button"
                className={styles.modalClose}
                onClick={() => {
                  setShowManageCountry(false);
                  setEditingCountryId(null);
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className={styles.modalList}>
              {loadingCountries ? (
                <div className={styles.empty}>Loading countries…</div>
              ) : countries.length === 0 ? (
                <div className={styles.empty}>No countries yet. Add one below.</div>
              ) : (
                countries.map((country) => (
                  <div key={country.id} className={styles.item}>
                    {editingCountryId === country.id ? (
                      <div className={styles.countryEditFields}>
                        <input
                          className={styles.inlineInput}
                          value={editCountryName}
                          onChange={(e) => setEditCountryName(e.target.value)}
                          placeholder="Country name"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveCountry(country.id);
                            if (e.key === "Escape") setEditingCountryId(null);
                          }}
                        />
                        <input
                          className={styles.inlineInput}
                          value={editCountryCode}
                          onChange={(e) => setEditCountryCode(e.target.value)}
                          placeholder="Code"
                        />
                        <input
                          className={styles.inlineInput}
                          value={editCountryPhone}
                          onChange={(e) => setEditCountryPhone(e.target.value)}
                          placeholder="Phone"
                        />
                      </div>
                    ) : (
                      <div className={styles.countryMeta}>
                        <span className={styles.itemLabel}>{country.name}</span>
                        <span className={styles.countrySub}>
                          {[country.code, country.phone].filter(Boolean).join(" · ")}
                        </span>
                      </div>
                    )}
                    <div className={styles.itemActions}>
                      {editingCountryId === country.id ? (
                        <>
                          <button
                            type="button"
                            className={styles.linkBtn}
                            disabled={saving}
                            onClick={() => saveCountry(country.id)}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className={styles.linkBtnMuted}
                            onClick={() => setEditingCountryId(null)}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className={styles.linkBtn}
                            disabled={saving}
                            onClick={() => {
                              setEditingCountryId(country.id);
                              setEditCountryName(country.name);
                              setEditCountryCode(country.code || "");
                              setEditCountryPhone(country.phone || "");
                            }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className={styles.linkBtnDanger}
                            disabled={saving}
                            onClick={() => removeCountry(country)}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className={styles.countryAddRow}>
              <input
                className={styles.input}
                placeholder="Country name"
                value={newCountryName}
                disabled={saving}
                onChange={(e) => setNewCountryName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") addCountry();
                }}
              />
              <input
                className={styles.input}
                placeholder="Code (optional)"
                value={newCountryCode}
                disabled={saving}
                onChange={(e) => setNewCountryCode(e.target.value)}
              />
              <input
                className={styles.input}
                placeholder="Phone (optional)"
                value={newCountryPhone}
                disabled={saving}
                onChange={(e) => setNewCountryPhone(e.target.value)}
              />
              <button type="button" className={styles.addBtn} disabled={saving} onClick={addCountry}>
                Add Country
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StateCityManager;
